import { useCallback, useContext, useEffect, useMemo } from "react";
import { useAddSocketListener } from "~/hooks/useAddSocketListener";
import { COPOLIT_SOCKET_KEY } from "~/constants/socket";
import { STEEL_SOCKET_URL } from "~/constants/url";
import { useAlerts } from "~/hooks/useAlerts";
import NotificationBanner from "../notificationBanner/NotificationBanner";
import { IAlertMessage } from "~/types/alert";
import { notification } from "antd";
import { CopilotMessageContext } from "~/providers/CopilotMessageProvider";
import { COPILOT_EVENTS } from "~/constants/events";
import { getConversationByResponseId } from "~/pages/copilotMessage/services/copilot";
import { isConversationGeneric } from "~/pages/copilotMessage/components/conversationDetail/components/conversationMessages/components/mikeMessage/helpers/mikeMessageParametersHelper";

interface AlertHandlerProps {}

const AlertHandler: React.FC<AlertHandlerProps> = () => {
  const { fetchAlerts, alerts, acknowledgeAlert, acknowledgeLoading } =
    useAlerts();

  const { conversationList, setSelectedConversation, setConversationResponse } =
    useContext(CopilotMessageContext);

  const messages = useMemo<IAlertMessage[]>(
    () =>
      alerts.map((alert) => ({
        id: alert.id,
        message: alert.message,
      })),
    [alerts],
  );

  const handleAcknowledge = useCallback(
    async (alertId: string) => {
      try {
        await acknowledgeAlert([alertId]);
        window.dispatchEvent(
          new CustomEvent(COPILOT_EVENTS.NOTIFICATION_ACKNOWLEDGED),
        );
      } catch (error) {
        notification.error({
          message: "Failed to acknowledge alert",
        });
      }
    },
    [acknowledgeAlert],
  );

  const handleDetailsClick = useCallback(
    async (alertId: string) => {
      try {
        const currentAlert = alerts.find((alert) => alert.id === alertId);
        if (!currentAlert) return;

        if (isConversationGeneric(currentAlert.conversationId)) {
          // First, call ConversationByResponseId/{responseId} service
          const responseId = currentAlert.conversationResponseId;
          if (responseId) {
            const conversation = await getConversationByResponseId(responseId);

            // set it as selected conversation
            if (conversation) {
              setSelectedConversation(conversation);
            }
          }
        } else {
          // For other conversationIds, use the existing logic
          const relatedConversation = conversationList?.find(
            (x) => x.id == currentAlert.conversationId,
          );

          if (relatedConversation) {
            setSelectedConversation(relatedConversation);
          }
        }

        setConversationResponse({
          conversationId: currentAlert.conversationId,
          responseId: currentAlert.conversationResponseId,
        });
      } catch (error) {
        notification.error({
          message: "Error handling alert details",
          description:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
        throw error;
      }
    },
    [
      alerts,
      conversationList,
      setSelectedConversation,
      setConversationResponse,
    ],
  );

  const onAlertRaised = useCallback(async () => {
    try {
      await fetchAlerts();
    } catch (error) {
      notification.error({
        message: "Failed to fetch alerts",
      });
    }
  }, [fetchAlerts]);

  useAddSocketListener(STEEL_SOCKET_URL, COPOLIT_SOCKET_KEY, {
    OnCopilotAlertRaised: onAlertRaised,
  });

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationBanner
      messages={messages}
      showDetailsButton
      showAcknowledgeButton
      onAcknowledge={handleAcknowledge}
      onClickDetailsButton={handleDetailsClick}
      acknowledgeLoading={acknowledgeLoading}
    />
  );
};

export default AlertHandler;
